
/** @import {Renderer} from '../wgpu/Renderer.js'; */

/**
 * @typedef {Object} WebGLFence
 * @property {string} label - An optional label to identify the fence.
 * @property {WebGLSync} syncObject - The WebGL synchronization object associated with this fence.
 */

/**
 * The FenceManager class allows to create fences (synchronization primitives inserted into the
 * GPU command stream) and allows checking whether any of the created fences has not been reached yet
 * (i.e. if the GPU is still processing commands sent before creating the last fence).
 *
 * We use this to prevent having multiple frames in flight, which causes input lag.
 * Just relying on requestAnimationFrame is not enough, it is called while multiple frames are still in flight.
 */
export class WebGLFenceManager {
  /** @type {WebGLFence[]} */
  #activeFences;

  /** @type {WebGL2RenderingContext} */
  #context;

  /**
   * @param {WebGL2RenderingContext} context - The WebGL2 rendering context.
   */
  constructor(context) {
    this.#context = context;
    this.#activeFences = [];
  }

  /**
   * @returns {boolean} - Returns true if there are active fences that have not been signaled yet.
   */
  hasActiveFences() {
    for (let i = 0; i < this.#activeFences.length; i++) {
      const fence = this.#activeFences[i];
      if (this.#context.isFenceSignaled(fence.syncObject)) {
        this.#context.deleteFence(fence.syncObject);
        this.#activeFences.splice(i, 1);
        i--;
      }
    }
    return this.#activeFences.length > 0;
  }

  /**
   * @param {string} [label=''] - An optional label to identify the fence.
   */
  createFence(label = '') {
    const syncObject = this.#context.createFence();
    const fence = { syncObject, label };
    this.#activeFences.push(fence);
  }

  /**
   * Clears all active fences.
   *
   * This effectively resets the fence manager. It is typically used when a full reset
   * of the synchronization state is required (e.g. after a context lost).
   */
  clear() {
    for (let fence of this.#activeFences) {
      this.#context.deleteFence(fence.syncObject);
    }
    this.#activeFences = [];
  }
}


/**
 * @typedef {Object} WebGPUFence
 *
 * @property {Promise} promise - A promise that resolves when the fence is signaled.
 * @property {string} label - An optional label to identify the fence.
 */


export class WebGPUFenceManager {
  /** @type {Set<WebGPUFence>} */
  #activeFences = new Set();

  /** @type {Renderer} */
  #renderer;

  /**
   * @param {Renderer} renderContext
   */
  constructor(renderContext) {
    this.#renderer = renderContext;
  }

  /**
   * @returns {boolean} - Returns true if there are active fences that have not been signaled yet.
   */
  hasActiveFences() {
    return this.#activeFences.size > 0;
  }

  /**
   * @param {string} [label=''] - An optional label to identify the fence.
   */
  createFence(label = '') {
    // the WebGPU renderer collects several commands across render batches before submitting them.
    // We flush those here to ensure the fence is actually waiting for every batch rendered so far.
    // Don't flush transparent draws mid-frame, to make sure they get depth-tested correctly.
    this.#renderer.flushCommandQueue(/*flushTransparents=*/false);

    let promise = this.#renderer.getDevice().queue.onSubmittedWorkDone();

    let fence = {
      promise,
      label
    };
    this.#activeFences.add(fence);

    promise.catch((error) => {
      console.error(`Error in fence "${label}":`, error);
    }).finally(() => {
      this.#activeFences.delete(fence);
    });
  }

  /**
   * Clears all active fences.
   *
   * This effectively resets the fence manager. It is typically used when a full reset
   * of the synchronization state is required (e.g. after a context lost).
   */
  clear() {
    this.#activeFences.clear();
  }
}